import { useState, useEffect } from 'react';
import { Modal, Input, Checkbox, Textarea, useToast } from '@/shared/components/ui';
import { Save, X } from 'lucide-react';
import { tipoUsuarioService } from '../api/tipoUsuarioService';
import { ActualizarTipoUsuarioDto } from '@/core/sdk/modules/administracion/tipoUsuarioClient';
import { JsonFieldBuilder } from './JsonFieldBuilder';
import { LIMITES_VALIDACION, MENSAJES_ERROR } from '../constants';
import styles from './TipoUsuarioModals.module.css';

interface Props {
    isOpen: boolean;
    tipoUsuarioId: number;
    onClose: () => void;
    onSuccess: () => void;
}

export const EditTipoUsuarioModal = ({ isOpen, tipoUsuarioId, onClose, onSuccess }: Props) => {
    const [formData, setFormData] = useState<ActualizarTipoUsuarioDto>({
        nombre: '',
        descripcion: '',
        activo: true,
        camposJson: ''
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const { showToast } = useToast();

    useEffect(() => {
        loadTipoUsuario();
    }, [tipoUsuarioId]);

    const loadTipoUsuario = async () => {
        try {
            setLoadingData(true);
            const data = await tipoUsuarioService.getById(tipoUsuarioId);
            setFormData({
                nombre: data.nombre,
                descripcion: data.descripcion || '',
                activo: data.activo,
                camposJson: data.camposJson || ''
            });
        } catch (error: any) {
            showToast({
                type: 'error',
                message: error.message || 'Error al cargar tipo de usuario'
            });
            onClose();
        } finally {
            setLoadingData(false);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.nombre.trim()) {
            newErrors.nombre = MENSAJES_ERROR.NOMBRE_REQUERIDO;
        } else if (formData.nombre.length > LIMITES_VALIDACION.NOMBRE_MAX) {
            newErrors.nombre = MENSAJES_ERROR.NOMBRE_EXCEDE_LIMITE;
        }

        if (formData.descripcion && formData.descripcion.length > LIMITES_VALIDACION.DESCRIPCION_MAX) {
            newErrors.descripcion = MENSAJES_ERROR.DESCRIPCION_EXCEDE_LIMITE;
        }

        if (formData.camposJson && formData.camposJson.trim()) {
            try {
                JSON.parse(formData.camposJson);
            } catch {
                newErrors.nombre = MENSAJES_ERROR.JSON_INVALIDO;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            const dataToSend = {
                ...formData,
                camposJson: formData.camposJson?.trim() || undefined,
                descripcion: formData.descripcion?.trim() || undefined
            };

            await tipoUsuarioService.update(tipoUsuarioId, dataToSend);

            showToast({
                type: 'success',
                message: MENSAJES_ERROR.ACTUALIZADO_EXITOSO
            });

            onSuccess();
        } catch (error: any) {
            const errorMessage = error.response?.data?.message ||
                error.message ||
                MENSAJES_ERROR.ERROR_ACTUALIZAR;

            showToast({
                type: 'error',
                message: errorMessage
            });

            console.error('[EditTipoUsuario] Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: keyof ActualizarTipoUsuarioDto, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Editar Tipo de Usuario"
            size="lg"
            closeOnOverlayClick={false}
            headerActions={
                <>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.saveButton}`}
                        onClick={handleSubmit}
                        disabled={loading || loadingData}
                        title="Guardar cambios"
                        aria-label="Guardar cambios del tipo de usuario"
                    >
                        <Save size={18} strokeWidth={2.5} />
                    </button>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.cancelButton}`}
                        onClick={onClose}
                        disabled={loading}
                        title="Cancelar"
                        aria-label="Cancelar edición"
                    >
                        <X size={18} strokeWidth={2.5} />
                    </button>
                </>
            }
        >
            {loadingData ? (
                <div className={styles.loadingContainer}>
                    <p>Cargando datos...</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formRow}>
                        <div className={styles.formRowFlex}>
                            <Input
                                label="Nombre *"
                                value={formData.nombre}
                                onChange={(e) => handleChange('nombre', e.target.value)}
                                error={errors.nombre}
                                disabled={loading}
                            />
                        </div>
                        <div className={styles.formRowCheckbox}>
                            <Checkbox
                                label="Activo"
                                checked={formData.activo}
                                onChange={(checked) => handleChange('activo', checked)}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <Textarea
                        label="Descripción"
                        value={formData.descripcion || ''}
                        onChange={(e) => handleChange('descripcion', e.target.value)}
                        error={errors.descripcion}
                        fullWidth
                        disabled={loading}
                        resize="vertical"
                    />

                    <JsonFieldBuilder
                        value={formData.camposJson || ''}
                        onChange={(json) => handleChange('camposJson', json)}
                        error={errors.camposJson}
                    />
                </form>
            )}
        </Modal>
    );
};
